/**
 * Chassis-page Activity tab — recent jobs for this chassis.
 *
 * Operators drill into a specific job via the "details" link, which
 * navigates to /settings/jobs/<id> where the full per-target results
 * + transcript page lives. The in-page transcript drawer that v2
 * originally shipped was redundant with that page and was removed
 * in v0.14.3.
 *
 * Endpoint: ``GET /api/jobs?chassis_id={id}&limit=N``.
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "../../../../api/client";
import { LocalTime } from "../../../../lib/datetime";
import { Badge, type BadgeTone } from "../../../components/Badge";
import { JobActions } from "../../../components/JobActions";

type JobStatus =
  | "queued"
  | "queued-on-chassis"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "partial";

type JobRow = {
  id: string;
  chassis_id: number | null;
  rendered_name: string;
  scope: string;
  status: JobStatus;
  started_by_gigafy_username: string;
  started_at: string | null;
  completed_at: string | null;
  // v0.14.3 — fallback for the timestamp column when started_at is
  // null (legacy SSH-preflight-failed jobs). created_at has a NOT
  // NULL DB default, so it's always present.
  created_at: string | null;
  total_targets: number | null;
  completed_targets: number | null;
};

type JobsResponse = { jobs: JobRow[] };

const STATUS_TONE: Record<JobStatus, BadgeTone> = {
  queued: "neutral",
  "queued-on-chassis": "neutral",
  running: "info",
  succeeded: "success",
  failed: "danger",
  cancelled: "muted",
  partial: "warning",
};

function durationStr(started: string | null, completed: string | null): string {
  if (!started) return "—";
  const start = new Date(started).getTime();
  const end = completed ? new Date(completed).getTime() : Date.now();
  const sec = Math.max(0, Math.round((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `${min}m ${remSec}s`;
}

export function ActivityTab({ chassisId }: { chassisId: string }) {
  const jobs = useQuery<JobsResponse>({
    queryKey: ["v2", "chassis", chassisId, "jobs"],
    queryFn: async () => {
      const res = await api.get<JobsResponse>(
        `/jobs?chassis_id=${chassisId}&limit=20`,
      );
      return res.data;
    },
    refetchInterval: 5_000,
  });

  return (
    <section className="flex flex-col gap-3 rounded-lg bg-slate-900 shadow-border p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
        Recent activity
      </h2>
      {jobs.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : (jobs.data?.jobs.length ?? 0) === 0 ? (
        <p className="text-xs text-slate-500">No jobs run against this chassis yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1">Status</th>
              <th className="px-2 py-1">Job</th>
              <th className="px-2 py-1">By</th>
              <th className="px-2 py-1">Progress</th>
              <th className="px-2 py-1">Duration</th>
              <th className="px-2 py-1">Started</th>
              <th className="px-2 py-1 text-right">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60 text-slate-300">
            {(jobs.data?.jobs ?? []).map((j) => {
              // v0.14.3 — fall back to created_at when started_at is
              // null (legacy ssh-preflight-failed jobs from before
              // the always-stamp-started_at fix). Worker arms that
              // got past the preflight stamp started_at via COALESCE,
              // so the displayed time is "earliest known activity".
              const displayTs = j.started_at ?? j.created_at;
              return (
                <tr key={j.id}>
                  <td className="px-2 py-1.5">
                    <Badge tone={STATUS_TONE[j.status] ?? "neutral"}>
                      {j.status}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 font-mono">{j.rendered_name}</td>
                  <td className="px-2 py-1.5">{j.started_by_gigafy_username}</td>
                  <td className="px-2 py-1.5 text-[11px]">
                    {j.total_targets
                      ? `${j.completed_targets ?? 0}/${j.total_targets}`
                      : "—"}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">
                    {durationStr(j.started_at ?? j.created_at, j.completed_at)}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={displayTs} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <span className="inline-flex items-center justify-end gap-2">
                      <JobActions job={j} />
                      <Link
                        to={`/settings/jobs/${j.id}`}
                        className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                        title="Open the full job detail page (per-target results + parsed_data + transcript)"
                      >
                        details
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

